import {ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import {
  BackNavigationService,
  Command,
  CommandStore,
  showRequestErrors,
  ScannerStore,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_SCANNER,
  SOFTLINE_FEATURE_TITLE,
  TitleStore } from '@softline/application';
import {Store} from '@softline/core';
import {SOFTLINE_FEATURE_WORK_NOTE_COLLECTION, WorkNoteCollectionStore } from '../../arbeitsschein.shared';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable} from 'rxjs';
import {WorkNote} from '../../data/work-note';
import {CommonModule} from '@angular/common';
import {WorkNoteCardComponent} from '../../components/work-note-card/work-note-card.component';
import {WorkNoteQuery} from '../../data/work-note-query';
import {map} from 'rxjs/operators';

import { ChipComponent } from '@softline/ui-core';
import { LoadingSpinnerComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-work-note-list-page',
    templateUrl: './work-note-list-page.component.html',
    styleUrls: ['./work-note-list-page.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, WorkNoteCardComponent,
    ChipComponent,
    LoadingSpinnerComponent,
    TranslatePipe]
})
export class WorkNoteListPageComponent implements OnInit, OnDestroy {
  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
    WorkNoteCollectionStore.getters.loading
  );

  readonly workNotes$: Observable<readonly WorkNote[]> = this.store.observe(
    SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
    WorkNoteCollectionStore.getters.all
  );

  readonly queryProperties$ = this.activatedRoute.queryParamMap.pipe(
    map(queryParamMap => {
      const props: { label: string, value: string, type: 'date' | 'text' }[] = []

      if (queryParamMap?.has('from')) {
        props.push({ label: 'Von:', value: queryParamMap.get('from') ?? '', type: 'date' })
      }

      if (queryParamMap?.has('to')) {
        props.push({ label: 'Bis:', value: queryParamMap.get('to') ?? '', type: 'date' })
      }

      if (queryParamMap?.has('objectId')) {
        props.push({ label: 'Objekt-ID:', value: queryParamMap.get('objectId') ?? '', type: 'text' })
      }

      return props
    })
  );

  constructor(
    private store: Store,
    protected router: Router,
    private activatedRoute: ActivatedRoute,
    private backNavigationService: BackNavigationService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#WORK_NOTE.LIST.TITLE'
    );

    this.backNavigationService.set(this);
    const commands = this.createCommands();

    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.addSet,
      { name: WorkNoteListPageComponent, commands }
    );

    const queryParamMap = this.activatedRoute.snapshot.queryParamMap

    try {
      if (queryParamMap.has('from') && queryParamMap.has('to')) {
        const queryParams: WorkNoteQuery = {
          from: queryParamMap.get('from') ?? '',
          to: queryParamMap.get('to') ?? ''
        }

        if (queryParamMap.has('objectId') && !isNaN(+(queryParamMap.get('objectId') ?? ''))) {
          queryParams['objectId'] = +(queryParamMap.get('objectId') ?? '')
        }

        await this.store.dispatch(
          SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
          WorkNoteCollectionStore.actions.loadMany,
          {
            clear: true,
            queryParams: {
              ...queryParams,
              showErledigtWeb: true
            }
          }
        );
      } else {
        const queryParams = queryParamMap.has('kzMuster')
          ? { showKzmuster: queryParamMap.get('kzMuster') === 'true' }
          : undefined;

        await this.store.dispatch(
          SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
          WorkNoteCollectionStore.actions.loadMany,
          { clear: true, queryParams }
        );
      }
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
  ngOnDestroy() {
    this.backNavigationService.set(undefined);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      ''
    );
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      WorkNoteListPageComponent
    );
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/']);
  }

  protected createCommands(): Command[] {
    return [
      {
        class:
          'top-menu top-menu-right flex justify-center align-center self-center',
        icon: 'fa-regular fa-barcode-scan',
        name: 'Scannen',
        execute: async () => {
          const scan = await this.store.dispatch(
            SOFTLINE_FEATURE_SCANNER,
            ScannerStore.actions.scan,
            { labelType: 'code39' }
          );

          if (!scan) return;

          await this.store.dispatch(
            SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
            WorkNoteCollectionStore.actions.addFromScanResult,
            { scan }
          );
        } },
      /* {
        class:
          'top-menu top-menu-right flex justify-center align-center self-center',
        icon: 'fa-regular fa-plus',
        name: 'Neu',
        execute: async () => {
          await this.store.dispatch(
            SOFTLINE_FEATURE_MODAL,
            ModalStore.actions.open<
              CreateWorkNote | null,
              {
                onSubmit: (
                  params: CreateWorkNote
                ) => Promise<{ workNote?: WorkNote }>;
              }
            >(),
            {
              component: CreateWorkNoteDialogComponent,
              id: 'CREATE_WORK_NOTE',
              data: {
                onSubmit: async (params: CreateWorkNote) => {
                  if (!params)
                    return { workNote: undefined };

                  try {
                    const workNote = await this.store.dispatch(
                      SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
                      WorkNoteCollectionStore.actions.createWorkNote,
                      params as CreateWorkNote
                    );
                    return { workNote };
                  } catch (e) {
                    handleRequestErrors(this.store, e);
                    return { workNote: undefined };
                  }
                } } }
          );
        } }, */
    ];
  }

  async openDetails(workNote: WorkNote): Promise<void> {
    await this.router.navigate([workNote.id], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'preserve'
    });
  }
}
